import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientAPI, leadAPI, userAPI, phaseAPI, locationAPI, exportAPI, standardAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, getStatusColor, getClientTypeLabel, getAccreditationLabel, getPhaseLabel, ACCREDITATION_STANDARDS } from '../lib/utils';
import { PhaseTracker } from '../components/PhaseTracker';
import {
  Plus, Search, Building2, MapPin, Phone, Mail, Users, MoreHorizontal,
  Eye, UserPlus, CheckCircle, Download, RefreshCw, CalendarCheck, Award,
  Clock, History, Shield, Trash2, Edit2, KeyRound,
} from 'lucide-react';
import { ClientResetPasswordDialog } from '../components/ClientResetPasswordDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const CLIENT_TYPES = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'lab', label: 'Laboratory' },
  { value: 'diagnostic', label: 'Diagnostic Center' },
];

export const ClientsPage = () => {
  const navigate = useNavigate();
  usePageTitle('Clients');
  const location = useLocation();
  const { isAdmin, isStaff } = useAuth();
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isRenewOpen, setIsRenewOpen] = useState(false);
  const [isValidityOpen, setIsValidityOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [clientPhases, setClientPhases] = useState([]);
  const [applications, setApplications] = useState([]);
  const [resetClient, setResetClient] = useState(null);

  const leadData = location.state?.lead;

  const [formData, setFormData] = useState({
    organization_name: leadData?.organization_name || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    contact_person: leadData?.contact_person || '',
    email: leadData?.email || '',
    phone: leadData?.phone || '',
    client_type: leadData?.client_type || 'hospital',
    bed_strength: '',
    departments: '',
    nabh_type: 'full_accreditation',
    accreditation_standard: 'nabh_6th_new',
    mobile_numbers: [{ label: 'Management', number: '' }],
  });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [renewForm, setRenewForm] = useState({ accreditation_standard: '' });
  const [validityForm, setValidityForm] = useState({ accreditation_valid_until: '', next_assessment_date: '' });
  const [dynamicStandards, setDynamicStandards] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [clientsRes, staffRes, stdsRes] = await Promise.all([
        clientAPI.getAll(),
        userAPI.getAll({ role: 'staff' }).catch(() => ({ data: [] })),
        standardAPI.getActive().catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data);
      setStaff(staffRes.data);
      if (stdsRes.data && stdsRes.data.length > 0) {
        setDynamicStandards(stdsRes.data.map(s => ({ value: s.code, label: s.label, group: s.group || 'Other' })));
      } else {
        setDynamicStandards(ACCREDITATION_STANDARDS);
      }
    } catch (error) {
      toast.error('Failed to load clients');
      setDynamicStandards(ACCREDITATION_STANDARDS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (leadData) setIsCreateOpen(true); }, [leadData]);

  const loadClientPhases = async (clientId) => {
    try {
      const res = await phaseAPI.getAll({ client_id: clientId });
      setClientPhases(res.data);
    } catch (error) {
      console.error('Failed to load phases:', error);
    }
  };

  const loadApplications = async (clientId) => {
    try {
      const res = await clientAPI.getApplications(clientId);
      setApplications(res.data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        bed_strength: formData.bed_strength ? parseInt(formData.bed_strength) : null,
        departments: formData.departments ? formData.departments.split(',').map(d => d.trim()) : [],
        mobile_numbers: (formData.mobile_numbers || []).filter(m => m.number),
        lead_id: leadData?.id,
      };
      if (leadData?.id) {
        await leadAPI.convert(leadData.id, payload);
        toast.success('Lead converted to client!');
      } else {
        await clientAPI.create(payload);
        toast.success('Client created with auto-generated phases!');
      }
      setIsCreateOpen(false);
      setFormData({
        organization_name: '', address: '', city: '', state: '', pincode: '',
        contact_person: '', email: '', phone: '', client_type: 'hospital',
        bed_strength: '', departments: '', nabh_type: 'full_accreditation',
        accreditation_standard: 'nabh_6th_new',
        mobile_numbers: [{ label: 'Management', number: '' }],
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleApprove = async (clientId) => {
    try {
      await clientAPI.approve(clientId);
      toast.success('Client approved and NABH process started!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve client');
    }
  };

  const handleAssignStaff = async (clientId, staffIds) => {
    try {
      await clientAPI.assignStaff(clientId, staffIds);
      toast.success('Staff assigned successfully!');
      setIsAssignOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to assign staff');
    }
  };

  const handleRenew = async () => {
    if (!renewForm.accreditation_standard) {
      toast.error('Please select an accreditation standard');
      return;
    }
    try {
      const res = await clientAPI.renew(selectedClient.id, renewForm);
      toast.success(res.data.message);
      setIsRenewOpen(false);
      setRenewForm({ accreditation_standard: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start renewal');
    }
  };

  const handleSetValidity = async () => {
    try {
      await clientAPI.setValidity(selectedClient.id, validityForm);
      toast.success('Accreditation validity updated! WhatsApp notification sent.');
      setIsValidityOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update validity');
    }
  };

  const openClientDetail = async (client) => {
    setSelectedClient(client);
    await loadClientPhases(client.id);
    setIsDetailOpen(true);
  };

  const openRenew = (client) => {
    setSelectedClient(client);
    setRenewForm({ accreditation_standard: '' });
    setIsRenewOpen(true);
  };

  const openValidity = (client) => {
    setSelectedClient(client);
    setValidityForm({
      accreditation_valid_until: client.accreditation_valid_until || '',
      next_assessment_date: client.next_assessment_date || '',
    });
    setIsValidityOpen(true);
  };

  const openHistory = async (client) => {
    setSelectedClient(client);
    await loadApplications(client.id);
    setIsHistoryOpen(true);
  };

  const filteredClients = clients.filter(
    (client) =>
      client.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportClients = async () => {
    try {
      const res = await exportAPI.clients();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'clients_export.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Clients exported to Excel');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.organization_name}"?\n\nThis will permanently remove the client and ALL associated data (phases, tasks, visits, invoices, documents, etc.). This action cannot be undone.`)) {
      return;
    }
    try {
      await clientAPI.delete(client.id);
      toast.success(`Client "${client.organization_name}" deleted`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const openEditClient = (client) => {
    setEditForm({
      organization_name: client.organization_name || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      pincode: client.pincode || '',
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      bed_strength: client.bed_strength || '',
      mobile_numbers: client.mobile_numbers?.length ? client.mobile_numbers : [{ label: 'Management', number: '' }],
      _id: client.id,
    });
    setIsEditOpen(true);
  };

  const handleEditClient = async () => {
    try {
      const { _id, ...data } = editForm;
      data.bed_strength = data.bed_strength ? parseInt(data.bed_strength) : null;
      data.mobile_numbers = (data.mobile_numbers || []).filter(m => m.number);
      await clientAPI.update(_id, data);
      toast.success('Client updated');
      setIsEditOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const addMobileNumber = (formSetter) => {
    formSetter(f => ({ ...f, mobile_numbers: [...(f.mobile_numbers || []), { label: '', number: '' }] }));
  };

  const removeMobileNumber = (formSetter, idx) => {
    formSetter(f => ({ ...f, mobile_numbers: f.mobile_numbers.filter((_, i) => i !== idx) }));
  };

  const updateMobileNumber = (formSetter, idx, field, value) => {
    formSetter(f => ({
      ...f,
      mobile_numbers: f.mobile_numbers.map((m, i) => i === idx ? { ...m, [field]: value } : m),
    }));
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="clients-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-teal-600 via-cyan-500 to-sky-400 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Client Management</h1>
            <p className="text-teal-100">Manage hospitals, labs, and diagnostic centers</p>
          </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportClients} data-testid="export-clients-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Register New Client</DialogTitle>
                <DialogDescription>Enter the client organization details and select accreditation standard</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="organization_name">Organization Name *</Label>
                    <Input id="organization_name" value={formData.organization_name} onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })} required data-testid="client-org-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Type *</Label>
                    <Select value={formData.client_type} onValueChange={(value) => setFormData({ ...formData, client_type: value })}>
                      <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CLIENT_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Accreditation Standard */}
                  <div className="space-y-2">
                    <Label>Accreditation Standard *</Label>
                    <Select value={formData.accreditation_standard} onValueChange={(value) => setFormData({ ...formData, accreditation_standard: value })}>
                      <SelectTrigger data-testid="accreditation-standard-select"><SelectValue placeholder="Select standard" /></SelectTrigger>
                      <SelectContent>
                        {[...new Set(dynamicStandards.map(s => s.group))].map(group => (
                          <React.Fragment key={group}>
                            <SelectItem value={`__group_${group}`} disabled className="text-xs font-semibold text-muted-foreground bg-muted/50 cursor-default">{group}</SelectItem>
                            {dynamicStandards.filter(s => s.group === group).map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Address *</Label>
                    <Textarea id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required rows={2} data-testid="client-address-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required data-testid="client-city-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} required data-testid="client-state-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Pincode *</Label>
                    <Input value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} required data-testid="client-pincode-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person *</Label>
                    <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} required data-testid="client-contact-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="client-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="client-phone-input" />
                  </div>
                  {formData.client_type === 'hospital' && (
                    <div className="space-y-2">
                      <Label>Bed Strength</Label>
                      <Input type="number" value={formData.bed_strength} onChange={(e) => setFormData({ ...formData, bed_strength: e.target.value })} data-testid="client-beds-input" />
                    </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label>Departments (comma-separated)</Label>
                    <Input value={formData.departments} onChange={(e) => setFormData({ ...formData, departments: e.target.value })} placeholder="e.g., Cardiology, Radiology, Pathology" data-testid="client-departments-input" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Additional Mobile Numbers</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => addMobileNumber(setFormData)}>+ Add</Button>
                    </div>
                    {(formData.mobile_numbers || []).map((m, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input className="w-1/3" value={m.label} onChange={e => updateMobileNumber(setFormData, i, 'label', e.target.value)} placeholder="Label (Owner)" />
                        <Input className="flex-1" value={m.number} onChange={e => updateMobileNumber(setFormData, i, 'number', e.target.value)} placeholder="Mobile number" />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeMobileNumber(setFormData, i)} className="text-red-500 shrink-0">X</Button>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-client-btn">Register Client</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search clients..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-clients-input" />
      </div>

      {/* Clients List (Table view) */}
      {filteredClients.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No clients found. Register your first client to get started!</p>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Standard</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Current Phase</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map(client => (
                  <TableRow key={client.id} data-testid={`client-row-${client.id}`} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-primary/10 rounded">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[200px]">{client.organization_name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{client.contact_person}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{getClientTypeLabel(client.client_type)}</Badge></TableCell>
                    <TableCell className="text-xs max-w-[160px] truncate" title={getAccreditationLabel(client.accreditation_standard)}>
                      {client.accreditation_standard ? getAccreditationLabel(client.accreditation_standard) : '-'}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" /><span className="truncate max-w-[140px]">{client.email}</span></div>
                      <div className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" /><span>{client.phone}</span></div>
                    </TableCell>
                    <TableCell className="text-xs"><div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-muted-foreground" />{client.city}, {client.state}</div></TableCell>
                    <TableCell>
                      <Badge className={client.is_approved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {client.is_approved ? 'Active' : 'Pending'}
                      </Badge>
                      {client.all_phases_completed && (
                        <Badge className="bg-emerald-100 text-emerald-800 mt-1 block w-fit text-[10px]"><Award className="w-2.5 h-2.5 mr-0.5 inline" /> Complete</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {client.is_approved && !client.all_phases_completed ? (
                        <Badge className={getStatusColor(client.current_phase?.includes('gap') ? 'in_progress' : 'pending')}>
                          {getPhaseLabel(client.current_phase)}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-xs">{client.accreditation_valid_until ? formatDate(client.accreditation_valid_until) : '-'}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-menu-${client.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openClientDetail(client)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          {isAdmin() && (
                            <DropdownMenuItem onClick={() => openEditClient(client)}>
                              <Edit2 className="w-4 h-4 mr-2" /> Edit Client
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openHistory(client)}>
                            <History className="w-4 h-4 mr-2" /> Application History
                          </DropdownMenuItem>
                          {isAdmin() && (
                            <DropdownMenuItem onClick={() => setResetClient(client)} data-testid={`reset-password-client-${client.id}`}>
                              <KeyRound className="w-4 h-4 mr-2" /> Reset Password
                            </DropdownMenuItem>
                          )}
                          {(isAdmin() || isStaff()) && (
                            <DropdownMenuItem onClick={() => { setSelectedClient(client); setIsAssignOpen(true); }}>
                              <UserPlus className="w-4 h-4 mr-2" /> Assign Staff
                            </DropdownMenuItem>
                          )}
                          {!client.is_approved && isAdmin() && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleApprove(client.id)} className="text-green-600">
                                <CheckCircle className="w-4 h-4 mr-2" /> Approve & Start
                              </DropdownMenuItem>
                            </>
                          )}
                          {client.all_phases_completed && (isAdmin() || isStaff()) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openValidity(client)} className="text-blue-600">
                                <CalendarCheck className="w-4 h-4 mr-2" /> Set Validity
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openRenew(client)} className="text-purple-600">
                                <RefreshCw className="w-4 h-4 mr-2" /> Renew Accreditation
                              </DropdownMenuItem>
                            </>
                          )}
                          {isAdmin() && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDeleteClient(client)} className="text-red-600" data-testid={`delete-client-${client.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete Client
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Client Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedClient?.organization_name}</DialogTitle>
            <DialogDescription>Client profile and NABH progress</DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{getClientTypeLabel(selectedClient.client_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Accreditation Standard</p>
                  <p className="font-medium text-sm">{getAccreditationLabel(selectedClient.accreditation_standard)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={selectedClient.is_approved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                    {selectedClient.is_approved ? 'Active' : 'Pending'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Application #</p>
                  <p className="font-medium">{selectedClient.application_number || 1}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{selectedClient.contact_person}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-sm">{selectedClient.email}</p>
                </div>
                {selectedClient.accreditation_valid_until && (
                  <div>
                    <p className="text-sm text-muted-foreground">Valid Until</p>
                    <p className="font-medium text-green-700">{formatDate(selectedClient.accreditation_valid_until)}</p>
                  </div>
                )}
                {selectedClient.next_assessment_date && (
                  <div>
                    <p className="text-sm text-muted-foreground">Next Assessment</p>
                    <p className="font-medium text-blue-700">{formatDate(selectedClient.next_assessment_date)}</p>
                  </div>
                )}
                <div className="col-span-2 md:col-span-3">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{selectedClient.address}, {selectedClient.city}, {selectedClient.state} - {selectedClient.pincode}</p>
                </div>
              </div>

              {/* Action buttons for completed clients */}
              {selectedClient.all_phases_completed && (isAdmin() || isStaff()) && (
                <div className="flex gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Award className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-800">All phases completed!</p>
                    <p className="text-xs text-emerald-600 mt-0.5">Set validity period or start a renewal application</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setIsDetailOpen(false); openValidity(selectedClient); }}>
                    <CalendarCheck className="w-4 h-4 mr-1" /> Set Validity
                  </Button>
                  <Button size="sm" onClick={() => { setIsDetailOpen(false); openRenew(selectedClient); }}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Renew
                  </Button>
                </div>
              )}

              {selectedClient.is_approved && (
                <div>
                  <h4 className="font-medium mb-4">NABH Accreditation Progress</h4>
                  <PhaseTracker
                    phases={clientPhases}
                    currentPhase={selectedClient.current_phase}
                    onPhaseClick={(phase) => navigate(`/phases?client=${selectedClient.id}&phase=${phase.id}`)}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ClientResetPasswordDialog client={resetClient} onClose={() => setResetClient(null)} />

      {/* Assign Staff Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Staff</DialogTitle>
            <DialogDescription>Select staff members to assign to this client</DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <StaffAssignmentForm
              staff={staff}
              currentAssigned={selectedClient.assigned_staff || []}
              onSubmit={(staffIds) => handleAssignStaff(selectedClient.id, staffIds)}
              onCancel={() => setIsAssignOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Renewal Dialog */}
      <Dialog open={isRenewOpen} onOpenChange={setIsRenewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renew Accreditation</DialogTitle>
            <DialogDescription>
              Start a new accreditation application for {selectedClient?.organization_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedClient?.accreditation_standard && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Current Standard</p>
                <p className="text-sm font-medium">{getAccreditationLabel(selectedClient.accreditation_standard)}</p>
                <p className="text-xs text-muted-foreground mt-1">Application #{selectedClient.application_number || 1}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>New Accreditation Standard *</Label>
              <Select value={renewForm.accreditation_standard} onValueChange={v => setRenewForm({ accreditation_standard: v })}>
                <SelectTrigger data-testid="renew-standard-select"><SelectValue placeholder="Select standard" /></SelectTrigger>
                <SelectContent>
                  {[...new Set(dynamicStandards.map(s => s.group))].map(group => (
                    <React.Fragment key={group}>
                      <SelectItem value={`__group_${group}`} disabled className="text-xs font-semibold text-muted-foreground bg-muted/50">{group}</SelectItem>
                      {dynamicStandards.filter(s => s.group === group).map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p>This will:</p>
              <ul className="list-disc ml-4 mt-1 text-xs space-y-1">
                <li>Archive current phases and tasks</li>
                <li>Create new phases based on the selected standard</li>
                <li>Start a fresh application cycle</li>
                <li>Previous application data will remain viewable in history</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenewOpen(false)}>Cancel</Button>
            <Button onClick={handleRenew} data-testid="renew-submit-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Start Renewal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validity Dialog */}
      <Dialog open={isValidityOpen} onOpenChange={setIsValidityOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Accreditation Validity</DialogTitle>
            <DialogDescription>
              Set validity period and next assessment date for {selectedClient?.organization_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Accreditation Valid Until *</Label>
              <Input type="date" value={validityForm.accreditation_valid_until} onChange={e => setValidityForm(f => ({ ...f, accreditation_valid_until: e.target.value }))} data-testid="validity-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Next Assessment Plan After *</Label>
              <Input type="date" value={validityForm.next_assessment_date} onChange={e => setValidityForm(f => ({ ...f, next_assessment_date: e.target.value }))} data-testid="assessment-date-input" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              A WhatsApp message will be auto-sent to the client and admin with the validity details.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsValidityOpen(false)}>Cancel</Button>
            <Button onClick={handleSetValidity} data-testid="validity-submit-btn">
              <CalendarCheck className="w-4 h-4 mr-1" /> Save & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application History Dialog */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Application History</DialogTitle>
            <DialogDescription>{selectedClient?.organization_name} - All accreditation applications</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {applications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No applications found</p>
            ) : applications.map(app => (
              <div key={app.id} className={`p-3 rounded-lg border ${app.status === 'active' ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`app-history-${app.application_number}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Application #{app.application_number}</span>
                  <Badge className={app.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                    {app.status === 'active' ? 'Active' : 'Completed'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{getAccreditationLabel(app.accreditation_standard)}</p>
                <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                  <span>Started: {formatDate(app.created_at)}</span>
                  {app.completed_at && <span>Completed: {formatDate(app.completed_at)}</span>}
                  {app.accreditation_valid_until && <span>Valid until: {formatDate(app.accreditation_valid_until)}</span>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>Update client information</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input value={editForm.organization_name || ''} onChange={e => setEditForm(f => ({ ...f, organization_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input value={editForm.contact_person || ''} onChange={e => setEditForm(f => ({ ...f, contact_person: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Address</Label>
              <Input value={editForm.address || ''} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={editForm.city || ''} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input value={editForm.state || ''} onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Pincode</Label>
              <Input value={editForm.pincode || ''} onChange={e => setEditForm(f => ({ ...f, pincode: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Bed Strength</Label>
              <Input type="number" value={editForm.bed_strength || ''} onChange={e => setEditForm(f => ({ ...f, bed_strength: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label>Mobile Numbers (WhatsApp)</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => addMobileNumber(setEditForm)}>+ Add</Button>
              </div>
              {(editForm.mobile_numbers || []).map((m, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input className="w-1/3" value={m.label} onChange={e => updateMobileNumber(setEditForm, i, 'label', e.target.value)} placeholder="Label (Owner, Management)" />
                  <Input className="flex-1" value={m.number} onChange={e => updateMobileNumber(setEditForm, i, 'number', e.target.value)} placeholder="Mobile number" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeMobileNumber(setEditForm, i)} className="text-red-500 shrink-0">X</Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditClient} data-testid="save-edit-client-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StaffAssignmentForm = ({ staff, currentAssigned, onSubmit, onCancel }) => {
  const [selected, setSelected] = useState(currentAssigned);

  const toggleStaff = (staffId) => {
    setSelected((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {staff.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg">
            <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggleStaff(s.id)} />
            <div>
              <p className="font-medium">{s.name}</p>
              <p className="text-sm text-muted-foreground">{s.email}</p>
            </div>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(selected)}>Save Assignment</Button>
      </DialogFooter>
    </div>
  );
};

export default ClientsPage;
